import {useId} from 'react'
import {
  AlertIcon,
  CircleIcon,
  CheckCircleFillIcon,
  GitMergeIcon,
  CheckIcon,
  XIcon,
  ExclamationIcon,
} from '@primer/octicons-react'
import CircleOcticon from '@github-ui/circle-octicon'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {Label, NavList, Spinner} from '@primer/react'

import styles from './StackSection.module.css'
import {MergeBoxExpandable} from './common/MergeBoxExpandable'
import {MergeBoxSectionHeader} from './common/MergeBoxSectionHeader'
import {CenteredLoadingSpinner} from './common/CenteredLoadingSpinner'
import {HEADER_ICON_SIZE} from '../../constants'
import type {StackEntryPayload} from '../../page-data/payloads/merge-box'
import type {StackSectionStatusType} from '../../helpers/merge-box-status-calculator/stack-section-status'
import {useStackRebaseStatus, type StackRebasePullRequestStatus} from '../../hooks/mutations/use-stack-rebase-mutation'

export function StackSection({
  pullRequestId,
  status,
  entries,
}: {
  pullRequestId: string
  status: StackSectionStatusType
  entries: StackEntryPayload[]
}) {
  const stackSectionAriaId = useId()
  const [stackExpanded, setStackExpanded] = useSessionStorage<boolean>(
    `${pullRequestId}:stackExpanded`,
    status !== 'PASSED',
  )
  const {isRebasing, status: rebaseState} = useStackRebaseStatus()

  const otherEntriesToMerge = entries.filter(e => e.position === 'BEFORE' && e.pull.state !== 'MERGED')

  let title
  let subtitle
  if (isRebasing) {
    title = 'Rebasing stack…'

    if (rebaseState) {
      const count = Object.values(rebaseState).length
      subtitle = `Rebasing ${count} pull request${count === 1 ? '' : 's'}.`
    } else {
      subtitle = 'Starting rebase.'
    }
  } else if (status === 'FAILED' || status === 'BLOCKED') {
    title = 'Unable to merge as stack'
    subtitle = 'Some pull requests in the stack cannot be merged.'
  } else if (status === 'PASSED') {
    title = 'Ready to merge as stack'
    const count = otherEntriesToMerge.length
    if (count === 0) {
      subtitle = 'This is the first unmerged pull request in the stack.'
    } else {
      subtitle = `Merging this pull request will also merge ${count} pull request${count === 1 ? '' : 's'} below it.`
    }
  } else {
    title = 'Could not determine stack status'
    subtitle = 'Refresh the page to try again.'
  }

  return (
    <section
      aria-label="Stacked pull requests"
      aria-describedby={stackSectionAriaId}
      className="border-bottom color-border-subtle"
    >
      <MergeBoxSectionHeader
        headerId={stackSectionAriaId}
        title={title}
        subtitle={subtitle}
        icon={
          isRebasing ? (
            <CenteredLoadingSpinner className={styles.StackSectionLoadingSpinner} />
          ) : (
            <StatusIcon status={status} />
          )
        }
        expandableProps={{
          ariaLabel: stackExpanded ? 'Collapse stack details' : 'Expand stack details',
          isExpanded: stackExpanded,
          onToggle: () => {
            setStackExpanded(!stackExpanded)
          },
        }}
      />
      <MergeBoxExpandable isExpanded={stackExpanded}>
        <NavList className="tmp-m-3">
          {entries.map(e => (
            <NavList.Item key={e.pull.number} href={e.pull.url} aria-current={e.position === 'CURRENT'}>
              <EntryListItem entry={e} rebaseState={rebaseState[e.pull.number]} />
            </NavList.Item>
          ))}
        </NavList>
      </MergeBoxExpandable>
    </section>
  )
}

function EntryListItem({entry, rebaseState}: {entry: StackEntryPayload; rebaseState?: StackRebasePullRequestStatus}) {
  if (!entry) {
    return
  }

  return (
    <div className="d-flex flex-justify-between">
      <div>
        <EntryStatusIcon entry={entry} rebaseState={rebaseState} />{' '}
        <span className={entry.position === 'AFTER' ? 'fgColor-muted' : ''}>{entry.pull.title}</span>{' '}
        <span className="fgColor-muted">#{entry.pull.number}</span>
      </div>
      <EntryStatusLabel entry={entry} rebaseState={rebaseState} />
    </div>
  )
}

function EntryStatusLabel({
  entry,
  rebaseState,
}: {
  entry: StackEntryPayload
  rebaseState?: StackRebasePullRequestStatus
}) {
  if (rebaseState === 'SUCCEEDED') {
    return <Label variant="success">Rebased</Label>
  } else if (rebaseState === 'CONFLICTED') {
    return <Label variant="danger">Merge conflicts</Label>
  } else if (rebaseState === 'FAILED') {
    return <Label variant="danger">Rebase failed</Label>
  }

  if (entry.position === 'AFTER') {
    return
  } else if (entry.pull.state === 'MERGED') {
    return <Label variant="done">Merged</Label>
  } else if (entry.failedSubConditions && entry.failedSubConditions.length > 0) {
    return <Label variant="attention">Needs rebase</Label>
  } else {
    return <Label variant="success">Ready to merge</Label>
  }
}

function EntryStatusIcon({entry, rebaseState}: {entry: StackEntryPayload; rebaseState?: StackRebasePullRequestStatus}) {
  if (rebaseState === 'PENDING') {
    return <Spinner size="small" />
  }

  if (entry.position === 'AFTER') {
    return <CircleIcon size={16} className="color-fg-muted" aria-label="Will not merge: higher up the stack" />
  } else if (entry.pull.state === 'MERGED') {
    return <GitMergeIcon size={16} className="color-fg-done" aria-label="Already merged" />
  } else if (entry.failedSubConditions && entry.failedSubConditions.length > 0) {
    return <AlertIcon size={16} className="color-fg-attention" aria-label="Cannot merge" />
  } else {
    return <CheckCircleFillIcon size={16} className="color-fg-success" aria-label="Ready to merge" />
  }
}

function StatusIcon({status}: {status: StackSectionStatusType}) {
  let className
  let Icon
  switch (status) {
    case 'BLOCKED':
    case 'FAILED':
      Icon = XIcon
      className = 'bgColor-danger-emphasis fgColor-onEmphasis'
      break
    case 'PASSED':
      Icon = CheckIcon
      className = 'bgColor-success-emphasis fgColor-onEmphasis'
      break
    case 'UNKNOWN':
    default:
      Icon = ExclamationIcon
      className = 'bgColor-muted fgColor-onEmphasis'
      break
  }

  return <CircleOcticon className={className} size={HEADER_ICON_SIZE} icon={() => <Icon size={16} />} />
}
